import { useNavigate } from 'react-router-dom';
import ProductQuantityIncrement from './ProductQuantityIncrement';
import { useContext, useEffect, useRef, useState } from 'react';
import LoadingButton from './LoadingButton';
import CustomModal from './CustomModal';
import { UserContext } from '../context/userContext';
import { Tooltip } from './Tooltip';
import {
  ProductCardsProps,
  OutOfStock,
  ROLE,
  WishListResponse,
} from '../interface/interface';
import { AlertIcon, WishlistHeartIcon } from '../assets/svgImages';
import OutOfStockOverlay from './OutOfStockOverlay';
import { AllowedProducts } from './AllowedProducts';
import { createWishList } from '../service/createWishList';
import showToast from './ToastComponent';
import { useAuth } from 'react-oidc-context';
import { WishListContext } from '../context/wishListContext';
import Loading from './Loading';

const ProductCards = (props: ProductCardsProps) => {
  const {
    product,
    addProductToCart,
    removeProductFromCart,
    substitueCard = false,
    allowedProduct = false,
    isGuest,
    isNonScriptPage = false,
  } = props;

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [openSubstituteModal, setSubstituteModal] = useState<boolean>(false);
  const [suggestAccessoriesModal, setSuggestAccessories] =
    useState<boolean>(false);
  const [addProductModalLoading, setAddProductModalLoading] =
    useState<boolean>(false);
  const { userDetail, iseRxDown, setLoadingEnable } = useContext(UserContext);

  const [showTooltip, setShowTooltip] = useState(false);
  const [showInvalidMailTooltip, setShowInvalidMailTooltip] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const productNameRef = useRef<HTMLParagraphElement>(null);
  const { user } = useAuth();
  const { wishListData, callWishList, wishLoading } =
    useContext(WishListContext);

  useEffect(() => {
    const checkOverflow = () => {
      if (productNameRef.current) {
        const isOverflowing =
          productNameRef.current.scrollWidth >
            productNameRef.current.clientWidth ||
          productNameRef.current.scrollHeight >
            productNameRef.current.clientHeight;
        setIsOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  if (wishLoading) return <Loading />;

  const handleCreateWishList = async () => {
    try {
      if (!product) return;
      setLoadingEnable(true);

      const response = await createWishList(
        user?.access_token as string,
        product.id,
      );
      if (typeof response.status === 'number') {
        showToast({
          message: response.message,
          type: 'warning',
        });
        return;
      }
      showToast({
        message: response.message,
        type: 'info',
      });
      await callWishList();
    } catch (err) {
      throw err;
    } finally {
      setLoadingEnable(false);
    }
  };

  const hasSubstituteAvailable =
    !substitueCard &&
    product.substituteProduct &&
    !product.substitutions_not_available;

  const isWishlisted = Boolean(
    wishListData?.find(
      (wish: WishListResponse) => wish.product.id === product.id,
    ),
  );

  return (
    <>
      <li
        className="group relative flex flex-col rounded-xl border md:border-secondary-cornflowerblue-500
          bg-white text-left cursor-pointer h-full overflow-hidden 
          transition-all duration-200 ease-out
           origin-center"
        key={product.id}
      >
        {!product?.prescription_required ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCreateWishList();
            }}
            aria-label="Add to wishlist"
            className={`absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center z-[49]`}
          >
            <WishlistHeartIcon
              className={
                isWishlisted
                  ? 'w-6 h-6 fill-pink-500 stroke-pink-500'
                  : 'w-6 h-6 fill-none stroke-pink-500'
              }
            />
          </button>
        ) : (
          <></>
        )}

        <div className="flex flex-1 flex-col px-4 pt-5 pb-0">
          {/* Product image */}
          <div
            className="w-full flex items-center justify-center relative"
            onClick={() => {
              const isPatient = userDetail.role === ROLE.Patients;
              const route = isPatient
                ? `/product/patient/${product?.product_code}`
                : `/product/${product?.product_code}`;

              navigate(route, {
                state: {
                  from: substitueCard ? '/home' : window.location.pathname,
                  totalQty: product.productMaxQty,
                  substituteProduct: product.substituteProduct,
                  isSubstitute: substitueCard || allowedProduct,
                  isWishlisted: isWishlisted,
                  isNonScriptPage: isNonScriptPage,
                },
              });
            }}
          >
            <img
              className="w-full h-40 object-contain"
              src={product.image_url ?? '/default-image.png'}
              alt="product-image"
              style={{ objectFit: 'contain' }}
              id={`product-image-${product?.product_code}`}
            />
            <OutOfStockOverlay
              outOfStock={product.outOfStock}
              inventoryQuantity={product.inventory_quantity}
              hasSubstitute={Boolean(product?.substituteProduct)}
              isGuest={false}
            />
          </div>

          {/* Brand + product name */}
          <div className="flex flex-col text-left gap-1 mt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary-cornflowerblue-600">
              {product.product_brand}
            </p>
            <div className="flex justify-between">
              <p
                className="text-xs md:text-sm font-regular text-primary-blue-500 md:two-line-ellipsis relative leading-normal line-clamp-2 min-h-9"
                ref={productNameRef}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                {product.name}
              </p>
              <p className="text-xs text-grey-500 font-regular block md:hidden ml-2">
                {product.weight}
                {product.unit}
              </p>
            </div>
            {isOverflowing && showTooltip && (
              <Tooltip
                text={product.name}
                className="z-50 product-name-tooltip"
              />
            )}
          </div>

          <div className="flex md:justify-between justify-end items-center mt-6 mb-3">
            <p className="text-xs text-grey-500 font-regular hidden md:block">
              {product.weight}
              {product.unit}
            </p>
            <div className="flex items-baseline gap-2">
              {product.old_price ? (
                <span className="text-xs text-grey-600 line-through font-regular">
                  ${product.old_price}
                </span>
              ) : (
                <></>
              )}
              <span className="text-lg sm:text-md font-semibold text-primary-blue-500">
                ${product?.price}
              </span>
            </div>
          </div>

          {/* Invalid email state - inline, not hover-gated */}
          {product.orderedQuantity === 0 &&
            !substitueCard &&
            !isGuest &&
            !userDetail?.supportingParams?.hasValidEmail && (
              <div className="relative mb-3">
                <button
                  type="button"
                  className="bg-primary-red-400 text-white text-xs rounded-lg p-3 flex items-center mx-auto w-full justify-center cursor-not-allowed"
                  onMouseEnter={() => setShowInvalidMailTooltip(true)}
                  onMouseLeave={() => setShowInvalidMailTooltip(false)}
                >
                  <span>
                    <AlertIcon className="fill-white" />
                  </span>
                  <span className="ml-1.5">Ordering Unavailable</span>
                </button>
                {showInvalidMailTooltip && (
                  <Tooltip
                    text="You cannot place orders as your email is invalid. Please update your email address in your profile."
                    className="z-50 top-14 !w-full xl:!w-[350px]"
                  />
                )}
              </div>
            )}

          {allowedProduct && product.allowedProducts && (
            <div className="mb-3">
              <AllowedProducts
                product={product}
                addProductToCart={addProductToCart}
                removeProductFromCart={removeProductFromCart}
              />
            </div>
          )}
        </div>

        {/* Quantity stepper - full width, replaces the Add to Cart bar once in cart */}
        {(product.orderedQuantity > 0 || substitueCard) && (
          <div className="w-full">
            <ProductQuantityIncrement
              productCode={product.product_code}
              orderedQuantity={product.orderedQuantity}
              productOutOfStock={product.outOfStock}
              maxQuantity={product.productMaxQty}
              addProductToCart={addProductToCart}
              removeProductFromCart={removeProductFromCart}
              isProductTile={true}
            />
          </div>
        )}

        {/* Always-visible full-width Add to Cart / Order substitution bar */}
        {product.orderedQuantity === 0 &&
          !substitueCard &&
          userDetail?.supportingParams?.hasValidEmail &&
          (hasSubstituteAvailable ? (
            <button
              type="button"
              className={`w-full bg-secondary-cornflowerblue-600 text-white text-xs md:text-sm !font-semibold !py-4
                hover:bg-white hover:text-secondary-cornflowerblue-600 transition-colors
                disabled:bg-slate-400 disabled:cursor-not-allowed hover:border-t hover:border-secondary-cornflowerblue-600`}
              onClick={(e) => {
                e.stopPropagation();
                setSubstituteModal(true);
              }}
              disabled={!userDetail?.supportingParams?.isUserAllowedToOrder}
            >
              Order substitution
            </button>
          ) : (
            <LoadingButton
              isProductTile
              buttonText="Add to Cart"
              isLoading={loading}
              className="!rounded-none !py-4 text-xs md:text-sm"
              isDisabled={
                product.outOfStock !== OutOfStock.false ||
                !userDetail?.supportingParams?.isUserAllowedToOrder ||
                iseRxDown
              }
              onClick={async () => {
                setLoading(true);
                await addProductToCart(product?.product_code);
                setLoading(false);
              }}
            />
          ))}
      </li>

      {/* Modals rendered safely outside the card root container */}
      <CustomModal
        isOpen={openSubstituteModal}
        onClose={() => setSubstituteModal(false)}
        header={''}
        footer={''}
        modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4 3xl:w-1/5"
      >
        <div>
          <p className="mb-3 text-sm font-regular text-primary-blue-500 text-left">
            Substitute product for:{' '}
            <span className="font-semibold">{product.name}</span>
          </p>
          <ProductCards
            product={product.substituteProduct!}
            addProductToCart={(id: string) =>
              addProductToCart(id, product?.product_code)
            }
            removeProductFromCart={removeProductFromCart}
            substitueCard={true}
            isAccessories={false}
            allowedProduct={false}
            isGuest={false}
            wishListProducts={wishListData}
          />
        </div>
      </CustomModal>

      <CustomModal
        isOpen={suggestAccessoriesModal}
        onClose={async () => {
          setLoading(true);
          setSuggestAccessories(false);
          await addProductToCart(product?.product_code);
          setLoading(false);
        }}
        header={'Dried Herb Product Added!'}
        footer={
          <>
            <LoadingButton
              isLoading={addProductModalLoading}
              className="!text-base !px-6 !py-3 !w-auto !rounded-md !mr-2"
              onClick={async () => {
                setAddProductModalLoading(true);
                await addProductToCart(product?.product_code);
                setAddProductModalLoading(false);
                navigate('/non-script-range');
              }}
              buttonText="Proceed"
            />
            <button
              className="text-base px-6 py-3 rounded-md !border !border-primary-blue-500"
              onClick={async () => {
                setSuggestAccessories(false);
                setLoading(true);
                await addProductToCart(product?.product_code);
                setLoading(false);
              }}
            >
              Stay on this page
            </button>
          </>
        }
        modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4"
      >
        <div className="text-start">
          Would you like to purchase required accessories along with this ?
        </div>
      </CustomModal>
    </>
  );
};

export default ProductCards;
